import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * @param {Element | null | undefined} element
 * @returns {boolean}
 */
function isDeferredFilterContext(element) {
  return !!element?.closest('.facets--drawer');
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Merges facet data from every facets form in the section.
   * Horizontal layouts split filters (drawer) and sort (desktop bar) across forms.
   * @returns {FormData}
   */
  #createSectionFormData() {
    const section = this.closest('.shopify-section');
    const merged = new FormData();

    if (!section) {
      return new FormData(this.refs.facetsForm);
    }

    /** @type {string | undefined} */
    let sortBy;

    section.querySelectorAll('facets-form-component').forEach((component) => {
      if (!(component instanceof FacetsFormComponent)) return;

      const form = component.refs.facetsForm;
      if (!(form instanceof HTMLFormElement)) return;

      for (const [key, value] of new FormData(form).entries()) {
        if (key === 'sort_by') {
          if (typeof value === 'string' && value) sortBy = value;
        } else {
          merged.append(key, value);
        }
      }
    });

    section.querySelectorAll('[name="sort_by"]').forEach((field) => {
      if (field instanceof HTMLInputElement && field.disabled) return;
      if (field instanceof HTMLSelectElement && field.disabled) return;

      if (field instanceof HTMLInputElement && field.type === 'radio' && field.checked) {
        sortBy = field.value;
      } else if (field instanceof HTMLSelectElement) {
        sortBy = field.value;
      }
    });

    if (sortBy) {
      merged.set('sort_by', sortBy);
    }

    return merged;
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the merged section form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = this.#createSectionFormData()) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    const priceMin = newParameters.get('filter.v.price.gte');
    const priceMax = newParameters.get('filter.v.price.lte');

    if (priceMin === '') {
      newParameters.delete('filter.v.price.gte');
    } else if (priceMin) {
      newParameters.set('filter.v.price.gte', formatMoney(priceMin));
    }

    if (priceMax === '') {
      newParameters.delete('filter.v.price.lte');
    } else if (priceMax) {
      newParameters.set('filter.v.price.lte', formatMoney(priceMax));
    }

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }

  /**
   * Syncs form inputs to match a URL without applying filters
   * @param {URL} [url]
   */
  syncFormFromURL(url = new URL(window.location.href)) {
    const { facetsForm } = this.refs;
    const params = url.searchParams;

    facetsForm.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.checked = params.getAll(input.name).includes(input.value);
    });

    const minInput = facetsForm.querySelector('input[name="filter.v.price.gte"]');
    const maxInput = facetsForm.querySelector('input[name="filter.v.price.lte"]');

    if (minInput instanceof HTMLInputElement) {
      minInput.value = params.get('filter.v.price.gte') ?? '';
    }

    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = params.get('filter.v.price.lte') ?? '';
    }

    this.#updatePendingSummaries();
  }

  #updatePendingSummaries() {
    this.querySelectorAll('facet-inputs-component').forEach((component) => {
      if (component instanceof FacetInputsComponent) {
        component.updateFilters();
      }
    });

    this.querySelectorAll('price-facet-component').forEach((component) => {
      if (component instanceof PriceFacetComponent) {
        component.syncRangeSlider();
        component.updateSummaryOnly();
      }
    });
  }

  /**
   * Discards pending filter changes and closes the drawer
   */
  cancelFiltersAndClose() {
    if (isDeferredFilterContext(this)) {
      this.syncFormFromURL();
    }

    this.#closeDrawer();
  }

  closeDrawerAfterApply() {
    this.#closeDrawer();
  }

  #closeDrawer() {
    const dialogComponent = this.closest('dialog-component');

    if (dialogComponent && 'closeDialog' in dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    this.#updateSelectedFacetSummary();

    if (isDeferredFilterContext(this)) return;

    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} [minRange] - The minimum price range input
 * @property {HTMLInputElement} [maxRange] - The maximum price range input
 * @property {HTMLElement} [rangeFill] - The active range track fill
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {HTMLElement | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#initRangeSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.#dialogComponent = null;
    this.#destroyRangeSlider();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#destroyRangeSlider();
    this.#initRangeSlider();
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#setMinAndMaxValues();
    this.#syncRangeFromInputs();
    this.#updateSummary();

    if (isDeferredFilterContext(this)) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  updateSummaryOnly() {
    this.#updateSummary();
  }

  syncRangeSlider() {
    this.#syncRangeFromInputs();
    this.#scheduleRangeFillUpdate();
  }

  #handleDialogOpen = () => {
    if (!this.isConnected || !this.closest('dialog')?.open) return;

    this.syncRangeSlider();
  };

  #scheduleRangeFillUpdate() {
    requestAnimationFrame(() => {
      this.#updateRangeFill();
    });
  }

  #initRangeSlider() {
    const { minRange, maxRange, minInput, maxInput } = this.refs;

    if (!(minRange instanceof HTMLInputElement) || !(maxRange instanceof HTMLInputElement)) return;

    this.#syncRangeFromInputs();
    this.#scheduleRangeFillUpdate();

    minRange.addEventListener('input', this.#onRangeInput);
    maxRange.addEventListener('input', this.#onRangeInput);
    minInput?.addEventListener('input', this.#syncRangeFromInputs);
    maxInput?.addEventListener('input', this.#syncRangeFromInputs);
    minInput?.addEventListener('change', this.#syncRangeFromInputs);
    maxInput?.addEventListener('change', this.#syncRangeFromInputs);
  }

  #destroyRangeSlider() {
    const { minRange, maxRange, minInput, maxInput } = this.refs;

    minRange?.removeEventListener('input', this.#onRangeInput);
    maxRange?.removeEventListener('input', this.#onRangeInput);
    minInput?.removeEventListener('input', this.#syncRangeFromInputs);
    maxInput?.removeEventListener('input', this.#syncRangeFromInputs);
    minInput?.removeEventListener('change', this.#syncRangeFromInputs);
    maxInput?.removeEventListener('change', this.#syncRangeFromInputs);
  }

  #onRangeInput = (event) => {
    const { minRange, maxRange, minInput, maxInput } = this.refs;

    if (
      !(minRange instanceof HTMLInputElement) ||
      !(maxRange instanceof HTMLInputElement) ||
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    let minVal = Number(minRange.value);
    let maxVal = Number(maxRange.value);

    if (minVal > maxVal) {
      if (event.target === minRange) {
        minVal = maxVal;
        minRange.value = String(minVal);
      } else {
        maxVal = minVal;
        maxRange.value = String(maxVal);
      }
    }

    minInput.value = String(minVal);
    maxInput.value = String(maxVal);

    this.#setMinAndMaxValues();
    this.#updateRangeFill();
    this.updateSummaryOnly();
  };

  #syncRangeFromInputs = () => {
    const { minRange, maxRange, minInput, maxInput } = this.refs;

    if (
      !(minRange instanceof HTMLInputElement) ||
      !(maxRange instanceof HTMLInputElement) ||
      !(minInput instanceof HTMLInputElement) ||
      !(maxInput instanceof HTMLInputElement)
    ) {
      return;
    }

    const rangeMax = Number(formatMoney(maxRange.max));
    const minVal = minInput.value.trim() === '' ? 0 : Number(formatMoney(minInput.value));
    const maxVal = maxInput.value.trim() === '' ? rangeMax : Number(formatMoney(maxInput.value));
    const clampedMin = Math.max(0, Math.min(minVal, maxVal));
    const clampedMax = Math.min(rangeMax, Math.max(minVal, maxVal));

    minRange.value = String(clampedMin);
    maxRange.value = String(clampedMax);
    this.#updateRangeFill();
  };

  #updateRangeFill() {
    const { minRange, maxRange, rangeFill } = this.refs;

    if (!(minRange instanceof HTMLInputElement) || !(rangeFill instanceof HTMLElement)) return;

    const min = Number(minRange.min);
    const max = Number(minRange.max);
    const range = max - min;

    if (range <= 0) return;

    const minVal = Number(minRange.value);
    const maxVal = Number(maxRange.value);
    const left = ((minVal - min) / range) * 100;
    const width = ((maxVal - minVal) / range) * 100;

    rangeFill.style.left = `${left}%`;
    rangeFill.style.width = `${width}%`;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    if (isDeferredFilterContext(this)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Updates the filter count bubble in the drawer after pills are removed.
 * @param {FacetsFormComponent} facetsForm
 */
function updateDrawerFilterBubble(facetsForm) {
  const drawer = facetsForm.closest('.facets--drawer');
  if (!drawer) return;

  const remainingPills = drawer.querySelectorAll('.facets-remove > facet-remove-component');
  const bubble = drawer.querySelector('.facets-drawer__title .facets__bubble');
  const hiddenCount = drawer.querySelector('.facets-drawer__title .visually-hidden');

  if (!bubble) return;

  if (remainingPills.length === 0) {
    bubble.remove();
    hiddenCount?.remove();
    return;
  }

  bubble.textContent = String(remainingPills.length);
}

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const targetUrl = new URL(url, window.location.origin);
    const isDeferred = isDeferredFilterContext(this);

    if (isDeferred) {
      event.preventDefault();
      event.stopPropagation();
      facetsForm.syncFormFromURL(targetUrl);

      document.querySelectorAll(`facet-remove-component[data-url="${CSS.escape(url)}"]`).forEach((pill) => {
        pill.remove();
      });

      updateDrawerFilterBubble(facetsForm);
      return;
    }

    document.querySelectorAll(`facet-remove-component[data-url="${CSS.escape(url)}"]`).forEach((pill) => {
      pill.remove();
    });

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles apply/cancel actions in the filter drawer
 * @extends {Component}
 */
class FacetsActionsComponent extends Component {
  /**
   * Applies pending filters and closes the drawer
   */
  applyFilters() {
    const facetsForm = this.closest('.facets--drawer')?.querySelector('facets-form-component');

    if (facetsForm instanceof FacetsFormComponent) {
      facetsForm.updateFilters();
      facetsForm.closeDrawerAfterApply();
    }
  }
}

if (!customElements.get('facets-actions-component')) {
  customElements.define('facets-actions-component', FacetsActionsComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  connectedCallback() {
    if (this.#usesMobileNativeSelectOnly()) {
      this.requiredRefs = [];
    }

    super.connectedCallback();
  }

  /**
   * Mobile horizontal sort uses a native select instead of the accordion dropdown.
   * @returns {boolean}
   */
  #usesMobileNativeSelectOnly() {
    return (
      this.dataset.shouldUseSelectOnMobile === 'true' &&
      this.dataset.testid === 'sorting-filter-component-mobile'
    );
  }

  /**
   * Finds the facets form component associated with this sorter.
   * @returns {FacetsFormComponent | null}
   */
  #getFacetsForm() {
    const closestForm = this.closest('facets-form-component');
    if (closestForm instanceof FacetsFormComponent) return closestForm;

    const associatedFormId =
      this.querySelector('[form]')?.getAttribute('form') ??
      this.querySelector('select[name="sort_by"]')?.getAttribute('form') ??
      this.querySelector('input[name="sort_by"]')?.getAttribute('form');

    if (associatedFormId) {
      const form = document.getElementById(associatedFormId);
      const facetsForm = form?.closest('facets-form-component');
      if (facetsForm instanceof FacetsFormComponent) return facetsForm;
    }

    const sectionForm = this.closest('.shopify-section')?.querySelector('facets-form-component');
    return sectionForm instanceof FacetsFormComponent ? sectionForm : null;
  }

  /**
   * Gets the sort field that triggered the update.
   * @param {Event} event
   * @returns {HTMLInputElement | HTMLSelectElement | null}
   */
  #getActiveSortField(event) {
    const path = event.composedPath?.() ?? [event.target];

    for (const node of path) {
      if (node instanceof HTMLInputElement && node.type === 'radio' && node.name === 'sort_by') {
        return node;
      }

      if (node instanceof HTMLSelectElement && node.name === 'sort_by') {
        return node;
      }
    }

    const checked = this.querySelector('input[name="sort_by"]:checked');
    if (checked instanceof HTMLInputElement) return checked;

    const select = this.querySelector('select[name="sort_by"]:not(:disabled)');
    if (select instanceof HTMLSelectElement) return select;

    return null;
  }

  /**
   * Disables other sort fields so only the active value is submitted.
   * @param {HTMLInputElement | HTMLSelectElement | null} activeField
   * @returns {(HTMLInputElement | HTMLSelectElement)[]}
   */
  #disableOtherSortFields(activeField) {
    const section = this.closest('.shopify-section');
    if (!section || !activeField) return [];

    /** @type {(HTMLInputElement | HTMLSelectElement)[]} */
    const disabledFields = [];

    section.querySelectorAll('[name="sort_by"]').forEach((field) => {
      if (field === activeField) return;
      if (field instanceof HTMLInputElement || field instanceof HTMLSelectElement) {
        if (!field.disabled) {
          field.disabled = true;
          disabledFields.push(field);
        }
      }
    });

    return disabledFields;
  }

  /**
   * Restores temporarily disabled sort fields.
   * @param {(HTMLInputElement | HTMLSelectElement)[]} disabledFields
   */
  #restoreDisabledFields(disabledFields) {
    disabledFields.forEach((field) => {
      field.disabled = false;
    });
  }

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm = this.#getFacetsForm();
    if (!facetsForm) return;

    const activeField = this.#getActiveSortField(event);
    const disabledFields = this.#disableOtherSortFields(activeField);

    try {
      facetsForm.updateFilters();
      this.updateFacetStatus(event);
    } finally {
      this.#restoreDisabledFields(disabledFields);
    }

    const { details } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    const activeField = this.#getActiveSortField(event);

    if (activeField instanceof HTMLInputElement && activeField.type === 'radio' && activeField.name === 'sort_by') {
      const sortValue =
        this.refs.sortValue instanceof HTMLElement
          ? this.refs.sortValue
          : this.querySelector('.facets__sort-value');

      if (sortValue instanceof HTMLElement) {
        sortValue.textContent = activeField.dataset.optionName ?? '';
      }
      return;
    }

    if (!(activeField instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      activeField.value !== details.dataset.defaultSortBy ? activeField.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
